import type { BlockConfigFile } from "@/src/models/intake/blocks/block-config";

export type IntakeManagerId = {
  id: number;
  label: string;
};

export const USRC_ADMIT_TYPES = [
  { id: 85, label: "NEW DIALYSIS PATIENT" },
  { id: 88, label: "TRANSFER IN FROM OTHER CENTER" },
  { id: 84, label: "TRANSIENT" },
  { id: 87, label: "INTERNAL TRANSFER" },
  { id: 1456, label: "INTERNAL TRANSIENT" },
  { id: 86, label: "MODALITY CHANGE" },
  { id: 1486, label: "NEW PATIENT – FOREIGN NATIONAL" },
  { id: 1495, label: "RESUME" },
  { id: 1496, label: "OUTGOING TRAVEL" },
  { id: 1497, label: "PATIENT REENGAGEMENT" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_DIAGNOSIS_TYPES = [
  { id: 57, label: "AKI" },
  { id: 58, label: "CHRONIC" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_MODALITY_TYPES = [
  { id: 1448, label: "AKI" },
  { id: 54, label: "CAPD" },
  { id: 55, label: "CCPD" },
  { id: 1416, label: "IN CENTER BACK-UP" },
  { id: 1457, label: "HOME HEMODIALYSIS" },
  { id: 53, label: "INCENTER HEMODIALYSIS" },
  { id: 56, label: "NXSTAGE HEMO" },
  { id: 1417, label: "STAFF ASSIST" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_GENDERS = [
  { id: 66, label: "MALE" },
  { id: 67, label: "FEMALE" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_DISCHARGE_DESTINATIONS = [
  { id: 80, label: "HOME/FAMILY MEMBER" },
  { id: 81, label: "LTAC" },
  { id: 82, label: "NURSING HOME" },
  { id: 83, label: "REHAB" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_NO_SSN_REASONS = [
  { id: 1, label: "No SSN (could not obtain)" },
  { id: 2, label: "Undocumented patient" },
  { id: 3, label: "Traveler out of country" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_REFERRAL_CHANNELS = [
  { id: 1, label: "Referral Manager - Careport" },
  { id: 2, label: "Email" },
  { id: 3, label: "Fax" },
  { id: 4, label: "Intake - Careport" },
  { id: 5, label: "Other Hospital System" },
  { id: 6, label: "Phone" },
  { id: 7, label: "USRC Website" },
  { id: 8, label: "Referral Portal" },
  { id: 9, label: "Valerie Health" },
] as const satisfies readonly IntakeManagerId[];

const CATALOGS_BY_FIELD_SUFFIX: Record<string, readonly IntakeManagerId[]> = {
  admitTypeId: USRC_ADMIT_TYPES,
  diagnosisId: USRC_DIAGNOSIS_TYPES,
  dischargeToId: USRC_DISCHARGE_DESTINATIONS,
  modalityId: USRC_MODALITY_TYPES,
  noSsnReasonId: USRC_NO_SSN_REASONS,
};

export function intakeManagerLabel(rows: readonly IntakeManagerId[], id: number): string {
  const match = rows.find((row) => row.id === id);

  if (!match) {
    throw new Error(`Intake Manager id ${id} is not in the catalog`);
  }

  return match.label;
}

export function intakeManagerId(rows: readonly IntakeManagerId[], label: string): number {
  const match = rows.find((row) => row.label === label);

  if (!match) {
    throw new Error(`Intake Manager label ${label} is not in the catalog`);
  }

  return match.id;
}

export function applyUsrcIntakeManagerCatalog(config: BlockConfigFile): BlockConfigFile {
  const blocks = { ...config.blocks };

  for (const [key, block] of Object.entries(blocks)) {
    if (!block.fields) continue;

    let changed = false;
    const fields = { ...block.fields };

    for (const [path, field] of Object.entries(fields)) {
      const rows = CATALOGS_BY_FIELD_SUFFIX[path.slice(path.lastIndexOf(".") + 1)];

      if (!rows || field.enabled === false) continue;

      const mappings = rows.map((row) => `${row.id} = ${row.label}`).join("; ");

      fields[path] = {
        ...field,
        description: field.description ? `${mappings}. ${field.description}` : mappings,
        options: rows.map((row) => String(row.id)),
        optionLabels: Object.fromEntries(rows.map((row) => [String(row.id), row.label])),
      };
      changed = true;
    }

    if (changed) blocks[key] = { ...block, fields };
  }

  return { ...config, blocks };
}
